import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router'
import { useUser } from '@clerk/clerk-react'
import { useThemeColors } from '../hooks/useThemeColors'
import {
  getInvestors,
  updateInvestor,
  createInvestor,
  createContact,
  updateContact,
  deleteContact,
  makeContactPrimary,
  logInvestorInteraction,
  triggerCalcomInvestorFollowup,
  type Investor,
  type InvestorContact,
  type InvestorStage,
} from '../lib/api'

const ADMIN_EMAILS = ['matt@primateintelligence.ai', 'mehdi@primateintelligence.ai']

const STAGES: { key: InvestorStage; label: string; color: string }[] = [
  { key: 'researching',    label: 'Researching',    color: '#7A7669' },
  { key: 'reached_out',   label: 'Reached Out',    color: '#2058F0' },
  { key: 'meeting_booked',label: 'Meeting Booked', color: '#38BDF8' },
  { key: 'pitched',       label: 'Pitched',        color: '#A78BFA' },
  { key: 'diligence',     label: 'Diligence',      color: '#FB923C' },
  { key: 'passed',        label: 'Passed',         color: '#F87171' },
  { key: 'committed',     label: 'Committed',      color: '#4ADE80' },
]

const FUND_STAGES = ['pre-seed', 'seed', 'series-a', 'series-b', 'growth', 'multi-stage']
const CONTACT_PATHS = ['cold_email', 'warm_intro', 'linkedin', 'conference', 'referral', 'other']

// ─── Helpers ──────────────────────────────────────────────────────────────────

function timeAgo(iso: string | null): string {
  if (!iso) return '—'
  const diff = Date.now() - new Date(iso).getTime()
  const days = Math.floor(diff / 86400000)
  if (days < 1) return 'today'
  if (days === 1) return '1d ago'
  if (days < 30) return `${days}d ago`
  const months = Math.floor(days / 30)
  return `${months}mo ago`
}

function stageColor(stage: InvestorStage): string {
  return STAGES.find(s => s.key === stage)?.color ?? '#9BA4B5'
}

function stageLabel(stage: InvestorStage): string {
  return STAGES.find(s => s.key === stage)?.label ?? stage
}

// ─── Add Investor Modal ───────────────────────────────────────────────────────

interface AddInvestorModalProps {
  onClose: () => void
  onSuccess: (investor: Investor) => void
}

function AddInvestorModal({ onClose, onSuccess }: AddInvestorModalProps) {
  const [name, setName] = useState('')
  const [firm, setFirm] = useState('')
  const [fundStage, setFundStage] = useState('')
  const [checkSize, setCheckSize] = useState('')
  const [contactPath, setContactPath] = useState('')
  const [stage, setStage] = useState<InvestorStage>('researching')
  const [email, setEmail] = useState('')
  const [linkedin, setLinkedin] = useState('')
  const [notes, setNotes] = useState('')
  const [nextAction, setNextAction] = useState('')
  const [nextActionDue, setNextActionDue] = useState('')
  const [lastContacted, setLastContacted] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit() {
    if (!firm.trim()) { setError('Fund / Firm is required'); return }
    if (!name.trim()) { setError('Contact Name is required'); return }
    setSaving(true)
    setError(null)
    try {
      const investor = await createInvestor({
        name: name.trim(),
        firm: firm.trim(),
        fund_stage: fundStage || null,
        check_size: checkSize || null,
        contact_path: contactPath || null,
        stage,
        email: email || null,
        linkedin: linkedin || null,
        notes: notes || null,
        next_action: nextAction || null,
        next_action_due: nextActionDue || null,
        last_contacted: lastContacted || null,
      })
      onSuccess(investor)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to create investor')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-void/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-obsidian border border-carbon rounded w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-carbon flex items-center justify-between sticky top-0 bg-obsidian">
          <h2 className="text-h3 text-chalk">Add Investor</h2>
          <button onClick={onClose} className="text-steel hover:text-chalk text-title-1 leading-none">&times;</button>
        </div>
        <div className="p-5 space-y-4">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded px-3 py-2 text-red-400 text-footnote">{error}</div>
          )}
          <div>
            <label className="block text-footnote text-steel mb-1">
              Firm Name <span className="text-red-400">*</span>
              <span className="text-steel font-normal ml-1">(individual name if solo angel)</span>
            </label>
            <input value={firm} onChange={e => setFirm(e.target.value)} placeholder="Horizon Ventures"
              className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal" />
          </div>
          <div>
            <label className="block text-footnote text-steel mb-1">First Contact Name <span className="text-red-400">*</span></label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="Sarah Chen"
              className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal" />
          </div>
          <div>
            <label className="block text-footnote text-steel mb-1">Fund Stage</label>
            <select value={fundStage} onChange={e => setFundStage(e.target.value)}
              className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal">
              <option value="">Select...</option>
              {FUND_STAGES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-footnote text-steel mb-1">Check Size</label>
            <input value={checkSize} onChange={e => setCheckSize(e.target.value)} placeholder="$500k–$1M"
              className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal" />
          </div>
          <div>
            <label className="block text-footnote text-steel mb-1">Contact Path</label>
            <select value={contactPath} onChange={e => setContactPath(e.target.value)}
              className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal">
              <option value="">Select...</option>
              {CONTACT_PATHS.map(p => <option key={p} value={p}>{p.replace(/_/g, ' ')}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-footnote text-steel mb-1">Stage</label>
            <select value={stage} onChange={e => setStage(e.target.value as InvestorStage)}
              className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal">
              {STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-footnote text-steel mb-1">Contact Email</label>
            <input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="sarah@horizonvc.com"
              className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal" />
          </div>
          <div>
            <label className="block text-footnote text-steel mb-1">Contact LinkedIn</label>
            <input value={linkedin} onChange={e => setLinkedin(e.target.value)} placeholder="https://linkedin.com/in/..."
              className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal" />
          </div>
          <div>
            <label className="block text-footnote text-steel mb-1">Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
              placeholder="Focus areas, background..."
              className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk placeholder:text-steel/50 focus:outline-none focus:border-signal resize-none" />
          </div>
          <div>
            <label className="block text-footnote text-steel mb-1">Next Action</label>
            <input value={nextAction} onChange={e => setNextAction(e.target.value)} placeholder="Send deck"
              className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-footnote text-steel mb-1">Next Action Due</label>
              <input type="date" value={nextActionDue} onChange={e => setNextActionDue(e.target.value)}
                className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal" />
            </div>
            <div>
              <label className="block text-footnote text-steel mb-1">Last Contacted</label>
              <input type="date" value={lastContacted} onChange={e => setLastContacted(e.target.value)}
                className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal" />
            </div>
          </div>
        </div>
        <div className="p-5 border-t border-carbon flex gap-3 justify-end sticky bottom-0 bg-obsidian">
          <button onClick={onClose}
            className="px-4 py-2 text-body-sm text-steel hover:text-chalk border border-carbon rounded transition-colors">
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={saving}
            className="px-4 py-2 text-body-sm bg-signal text-white rounded hover:bg-signal/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            {saving ? 'Adding…' : 'Add Investor'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Edit Investor Panel ──────────────────────────────────────────────────────

interface EditInvestorPanelProps {
  investor: Investor
  onClose: () => void
  onSaved: (updated: Investor) => void
  onContactsChanged: (investorId: string, contacts: InvestorContact[]) => void
}

function EditInvestorPanel({ investor, onClose, onSaved, onContactsChanged }: EditInvestorPanelProps) {
  const [firm, setFirm] = useState(investor.firm ?? '')
  const [fundStage, setFundStage] = useState(investor.fund_stage ?? '')
  const [checkSize, setCheckSize] = useState(investor.check_size ?? '')
  const [contactPath, setContactPath] = useState(investor.contact_path ?? '')
  const [stage, setStage] = useState<InvestorStage>(investor.stage)
  const [notes, setNotes] = useState(investor.notes ?? '')
  const [nextAction, setNextAction] = useState(investor.next_action ?? '')
  const [nextActionDue, setNextActionDue] = useState(investor.next_action_due ?? '')
  const [lastContacted, setLastContacted] = useState(investor.last_contacted ?? '')
  // Keep a local copy of contacts so edits within the panel are immediately reflected
  const [localContacts, setLocalContacts] = useState<InvestorContact[]>(investor.contacts ?? [])
  const [noteText, setNoteText] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notesSaved, setNotesSaved] = useState(false)
  const [savedNotes, setSavedNotes] = useState<{ text: string; ts: string }[]>([])

  async function handleSaveNote() {
    if (!noteText.trim()) return
    setSaving(true)
    setError(null)
    try {
      await logInvestorInteraction(investor.id, {
        channel: 'note',
        direction: 'outbound',
        notes: noteText.trim(),
        outcome: 'follow_up',
      })
      setSavedNotes(prev => [{ text: noteText.trim(), ts: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }, ...prev])
      setNoteText('')
      setNotesSaved(true)
      setTimeout(() => setNotesSaved(false), 2000)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setSaving(false)
    }
  }

  async function handleSave() {
    if (!firm.trim()) { setError('Firm is required'); return }
    setSaving(true)
    setError(null)
    try {
      const fields: Partial<Investor> = {
        firm: firm.trim(),
        fund_stage: fundStage || null,
        check_size: checkSize || null,
        contact_path: contactPath || null,
        stage,
        notes: notes || null,
        next_action: nextAction || null,
        next_action_due: nextActionDue || null,
        last_contacted: lastContacted || null,
      }
      const updated = await updateInvestor(investor.id, fields)
      onSaved({ ...investor, ...updated })
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-obsidian border-l border-carbon w-full max-w-md h-full overflow-y-auto shadow-xl flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-carbon sticky top-0 bg-obsidian z-10">
          <div>
            <h2 className="text-body font-semibold text-chalk">Edit Investor</h2>
            <p className="text-footnote text-steel mt-0.5">{investor.firm}</p>
          </div>
          <button onClick={onClose} className="text-steel hover:text-chalk text-title-1">✕</button>
        </div>

        <div className="flex-1 px-6 py-4 space-y-4 overflow-y-auto">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded px-3 py-2 text-red-400 text-footnote">{error}</div>
          )}

          {/* Quick note */}
          <div className="bg-signal/5 border border-signal/20 rounded p-3 space-y-2">
            <label className="block text-footnote text-signal font-medium">📝 Add a note</label>
            <textarea
              value={noteText}
              onChange={e => setNoteText(e.target.value)}
              placeholder="Quick note or update…"
              rows={2}
              className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk placeholder:text-steel/50 focus:outline-none focus:border-signal resize-none"
            />
            <button
              onClick={handleSaveNote}
              disabled={saving || !noteText.trim()}
              className="px-3 py-1.5 text-footnote bg-signal text-white rounded hover:bg-signal/90 disabled:opacity-40 transition-colors"
            >
              {notesSaved ? '✓ Saved' : 'Log Note'}
            </button>
            {savedNotes.length > 0 && (
              <div className="space-y-1 pt-1 border-t border-signal/20">
                {savedNotes.map((n, i) => (
                  <div key={i} className="flex gap-2 text-footnote">
                    <span className="text-steel shrink-0">{n.ts} 📝</span>
                    <span className="text-chalk">{n.text}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="border-t border-carbon pt-4">
            <div className="text-footnote text-steel font-medium uppercase tracking-wide mb-3">Firm Details</div>
          </div>

          <div>
            <label className="block text-footnote text-steel mb-1">
              Firm Name <span className="text-red-400">*</span>
            </label>
            <input value={firm} onChange={e => setFirm(e.target.value)}
              className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal" />
          </div>

          <div className="border-t border-carbon pt-4">
            <div className="text-footnote text-steel font-medium uppercase tracking-wide mb-3">Contacts</div>
            <ContactsList
              investorId={investor.id}
              contacts={localContacts}
              onContactsChanged={(investorId, contacts) => {
                setLocalContacts(contacts)
                onContactsChanged(investorId, contacts)
              }}
            />
          </div>

          <div className="border-t border-carbon pt-4">
            <div className="text-footnote text-steel font-medium uppercase tracking-wide mb-3">Classification</div>
          </div>

          <div>
            <label className="block text-footnote text-steel mb-1">Stage</label>
            <select value={stage} onChange={e => setStage(e.target.value as InvestorStage)}
              className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal">
              {STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-footnote text-steel mb-1">Fund Stage</label>
            <select value={fundStage} onChange={e => setFundStage(e.target.value)}
              className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal">
              <option value="">Select...</option>
              {FUND_STAGES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-footnote text-steel mb-1">Check Size</label>
            <input value={checkSize} onChange={e => setCheckSize(e.target.value)} placeholder="$500k–$1M"
              className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal" />
          </div>
          <div>
            <label className="block text-footnote text-steel mb-1">Contact Path</label>
            <select value={contactPath} onChange={e => setContactPath(e.target.value)}
              className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal">
              <option value="">Select...</option>
              {CONTACT_PATHS.map(p => <option key={p} value={p}>{p.replace(/_/g, ' ')}</option>)}
            </select>
          </div>
          <div className="border-t border-carbon pt-4">
            <div className="text-footnote text-steel font-medium uppercase tracking-wide mb-3">Context</div>
          </div>

          <div>
            <label className="block text-footnote text-steel mb-1">Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
              placeholder="Focus areas, thesis, background..."
              className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk placeholder:text-steel/50 focus:outline-none focus:border-signal resize-none" />
          </div>
          <div>
            <label className="block text-footnote text-steel mb-1">Next Action</label>
            <input value={nextAction} onChange={e => setNextAction(e.target.value)} placeholder="Send deck"
              className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-footnote text-steel mb-1">Next Action Due</label>
              <input type="date" value={nextActionDue} onChange={e => setNextActionDue(e.target.value)}
                className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal" />
            </div>
            <div>
              <label className="block text-footnote text-steel mb-1">Last Contacted</label>
              <input type="date" value={lastContacted} onChange={e => setLastContacted(e.target.value)}
                className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal" />
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-carbon flex gap-3 sticky bottom-0 bg-obsidian">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 bg-signal text-white px-4 py-2 rounded text-body-sm font-medium hover:bg-signal/90 disabled:opacity-50 transition-colors"
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
          <button onClick={onClose}
            className="px-4 py-2 text-steel hover:text-chalk text-body-sm transition-colors">
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}


// ─── Contacts List ────────────────────────────────────────────────────────────

function ContactsList({
  investorId,
  contacts,
  onContactsChanged,
}: {
  investorId: string
  contacts: InvestorContact[]
  onContactsChanged: (investorId: string, contacts: InvestorContact[]) => void
}) {
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editForm, setEditForm] = useState<{ name: string; email: string; linkedin: string; receives_updates: boolean }>({ name: '', email: '', linkedin: '', receives_updates: false })
  const [addForm, setAddForm] = useState({ name: '', email: '' })
  const [saving, setSaving] = useState(false)
  const [addError, setAddError] = useState<string | null>(null)

  function startEdit(c: InvestorContact) {
    setEditingId(c.id)
    setEditForm({ name: c.name, email: c.email ?? '', linkedin: c.linkedin ?? '', receives_updates: c.receives_updates })
  }

  async function handleUpdateContact(c: InvestorContact) {
    setSaving(true)
    try {
      const updated = await updateContact(investorId, c.id, {
        name: editForm.name,
        email: editForm.email || null,
        linkedin: editForm.linkedin || null,
        receives_updates: editForm.receives_updates,
      })
      onContactsChanged(investorId, contacts.map(x => x.id === c.id ? updated : x))
      setEditingId(null)
    } catch { /* silent */ } finally { setSaving(false) }
  }

  async function handleDeleteContact(c: InvestorContact) {
    if (contacts.length <= 1) return
    setSaving(true)
    try {
      await deleteContact(investorId, c.id)
      onContactsChanged(investorId, contacts.filter(x => x.id !== c.id))
    } catch { /* silent */ } finally { setSaving(false) }
  }

  async function handleMakePrimary(c: InvestorContact) {
    if (c.is_primary) return
    setSaving(true)
    try {
      await makeContactPrimary(investorId, c.id)
      // Optimistically update: set this contact primary, clear all others
      const updated = contacts.map(x => ({ ...x, is_primary: x.id === c.id }))
      onContactsChanged(investorId, updated)
    } catch { /* silent */ } finally { setSaving(false) }
  }

  async function handleAddContact() {
    if (!addForm.name.trim()) { setAddError('Name is required'); return }
    setAddError(null)
    setSaving(true)
    try {
      const newContact = await createContact(investorId, {
        name: addForm.name.trim(),
        email: addForm.email || null,
      })
      onContactsChanged(investorId, [...contacts, newContact])
      setAddForm({ name: '', email: '' })
    } catch (err) {
      setAddError(err instanceof Error ? err.message : 'Failed')
    } finally { setSaving(false) }
  }

  return (
    <div className="space-y-2">
      {(contacts ?? []).map(c => (
        <div key={c.id} className="border border-carbon rounded px-3 py-2">
          {editingId === c.id ? (
            <div className="space-y-2">
              <input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} placeholder="Name"
                className="w-full bg-void border border-carbon rounded px-2 py-1 text-footnote text-chalk focus:outline-none focus:border-signal" />
              <input value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} placeholder="Email" type="email"
                className="w-full bg-void border border-carbon rounded px-2 py-1 text-footnote text-chalk focus:outline-none focus:border-signal" />
              <input value={editForm.linkedin} onChange={e => setEditForm(f => ({ ...f, linkedin: e.target.value }))} placeholder="LinkedIn URL"
                className="w-full bg-void border border-carbon rounded px-2 py-1 text-footnote text-chalk focus:outline-none focus:border-signal" />
              <label className="flex items-center gap-1.5 text-footnote text-steel cursor-pointer">
                <input type="checkbox" checked={editForm.receives_updates}
                  onChange={e => setEditForm(f => ({ ...f, receives_updates: e.target.checked }))}
                  className="w-3 h-3" />
                Receives updates
              </label>
              <div className="flex gap-2">
                <button onClick={() => void handleUpdateContact(c)} disabled={saving}
                  className="px-2 py-1 text-footnote bg-signal text-white rounded hover:bg-signal/90 disabled:opacity-40">Save</button>
                <button onClick={() => setEditingId(null)} className="px-2 py-1 text-footnote text-steel hover:text-chalk">Cancel</button>
              </div>
            </div>
          ) : (
            <div className="flex items-start justify-between gap-2">
              <div className="text-footnote space-y-0.5 flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-chalk font-medium">{c.name}</span>
                  {c.is_primary && (
                    <span className="px-1 py-0.5 text-footnote bg-signal/15 text-signal rounded font-medium leading-none">Primary</span>
                  )}
                </div>
                {c.email && <div><a href={`mailto:${c.email}`} className="text-signal hover:underline">{c.email}</a></div>}
                {c.linkedin && <div><a href={c.linkedin} target="_blank" rel="noopener noreferrer" className="text-steel hover:text-signal truncate block">{c.linkedin}</a></div>}
                {c.receives_updates && <div className="text-green-400 text-footnote">📧 receives updates</div>}
              </div>
              <div className="flex gap-1 shrink-0">
                {contacts.length > 1 && !c.is_primary && (
                  <button
                    onClick={() => void handleMakePrimary(c)}
                    disabled={saving}
                    title="Make primary contact"
                    className="px-1 py-0.5 text-footnote bg-void text-steel hover:text-chalk border border-carbon rounded font-medium leading-none disabled:opacity-40"
                  >Make Primary</button>
                )}
                <button onClick={() => startEdit(c)} className="px-1.5 py-0.5 text-footnote text-steel hover:text-chalk border border-carbon rounded">✏</button>
                {contacts.length > 1 ? (
                  <button onClick={() => void handleDeleteContact(c)} disabled={saving}
                    className="px-1.5 py-0.5 text-footnote text-red-400 hover:text-red-300 border border-carbon rounded disabled:opacity-40">✕</button>
                ) : (
                  <button title="Cannot delete only contact"
                    className="px-1.5 py-0.5 text-footnote text-steel/30 border border-carbon/30 rounded cursor-not-allowed">✕</button>
                )}
              </div>
            </div>
          )}
        </div>
      ))}
      <div className="border border-dashed border-carbon/60 rounded px-3 py-2 space-y-2">
        <div className="text-footnote text-steel font-medium">+ Add Contact</div>
        {addError && <div className="text-red-400 text-footnote">{addError}</div>}
        <div className="flex gap-2 flex-wrap">
          <input value={addForm.name} onChange={e => setAddForm(f => ({ ...f, name: e.target.value }))} placeholder="Name *"
            className="flex-1 min-w-24 bg-void border border-carbon rounded px-2 py-1 text-footnote text-chalk focus:outline-none focus:border-signal" />
          <input value={addForm.email} onChange={e => setAddForm(f => ({ ...f, email: e.target.value }))} placeholder="Email" type="email"
            className="flex-1 min-w-24 bg-void border border-carbon rounded px-2 py-1 text-footnote text-chalk focus:outline-none focus:border-signal" />
          <button onClick={() => void handleAddContact()} disabled={saving || !addForm.name.trim()}
            className="px-2 py-1 text-footnote bg-signal text-white rounded hover:bg-signal/90 disabled:opacity-40 shrink-0">Add</button>
        </div>
      </div>
    </div>
  )
}

// ─── Investor Grid Table ───────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────

const INV_COLS = [
  { key: 'firm',            label: 'Firm / Contact', defaultW: 220, sortable: true },
  { key: 'stage',           label: 'Stage',          defaultW: 120, sortable: true },
  { key: 'fund_stage',      label: 'Fund Stage',     defaultW: 90,  sortable: true },
  { key: 'check_size',      label: 'Check Size',     defaultW: 100, sortable: true },
  { key: 'invested',        label: 'Invested',       defaultW: 70,  sortable: true },
  { key: 'last_touch',      label: 'Last touch',     defaultW: 100, sortable: true },
  { key: 'updates',         label: 'Updates',        defaultW: 64,  sortable: true },
  { key: 'next_action',     label: 'Next action',    defaultW: 160, sortable: false },
  { key: 'actions',         label: '',               defaultW: 80,  sortable: false },
]

const STAGE_ORDER: Record<string, number> = {
  researching: 0, reached_out: 1, meeting_booked: 2,
  pitched: 3, diligence: 4, passed: 5, committed: 6,
}

function buildInvGridTemplate(widths: number[]): string {
  return widths.map((w, i) => i === widths.length - 2 ? `minmax(${w}px,1fr)` : `${w}px`).join(' ')
}

function InvestorExpandPanel({
  investor, onEdit, onClose, onContactsChanged
}: {
  investor: Investor
  onEdit: (i: Investor) => void
  onClose: () => void
  onContactsChanged: (investorId: string, contacts: InvestorContact[]) => void
}) {
  const [noteText, setNoteText] = useState('')
  const [saving, setSaving] = useState(false)
  const [noteSaved, setNoteSaved] = useState(false)
  const [savedNotes, setSavedNotes] = useState<{ text: string; ts: string }[]>([])

  async function handleLogNote() {
    if (!noteText.trim()) return
    setSaving(true)
    try {
      await logInvestorInteraction(investor.id, { channel: 'note', direction: 'outbound', notes: noteText.trim(), outcome: 'follow_up' })
      setSavedNotes(prev => [{ text: noteText.trim(), ts: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }, ...prev])
      setNoteText('')
      setNoteSaved(true)
      setTimeout(() => setNoteSaved(false), 2000)
    } catch { /* silent */ } finally { setSaving(false) }
  }

  return (
    <div style={{ borderLeft: '3px solid rgba(32,88,240,0.5)', background: 'rgba(14,16,22,0.9)' }}
      className="px-4 py-3 space-y-3">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 space-y-2">
          <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-footnote">
            {investor.firm && investor.firm !== 'Personal' && <div><span className="text-steel">Firm: </span><span className="text-chalk">{investor.firm}</span></div>}
            {investor.fund_stage && <div><span className="text-steel">Fund stage: </span><span className="text-chalk capitalize">{investor.fund_stage}</span></div>}
            {investor.check_size && <div><span className="text-steel">Check size: </span><span className="text-chalk">{investor.check_size}</span></div>}
            {investor.contact_path && <div><span className="text-steel">Contact path: </span><span className="text-chalk capitalize">{investor.contact_path.replace(/_/g, ' ')}</span></div>}
            {investor.next_action && <div><span className="text-steel">Next: </span><span className="text-signal">{investor.next_action}</span></div>}
            {investor.next_action_due && <div><span className="text-steel">Due: </span><span className="text-chalk">{investor.next_action_due}</span></div>}
            {investor.notes && <div className="col-span-2"><span className="text-steel">Notes: </span><span className="text-chalk">{investor.notes}</span></div>}
          </div>
          <div>
            <div className="text-footnote text-steel font-medium mb-1">Contacts</div>
            <ContactsList
              investorId={investor.id}
              contacts={investor.contacts ?? []}
              onContactsChanged={onContactsChanged}
            />
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <button onClick={() => onEdit(investor)} className="px-2 py-1 text-footnote text-steel hover:text-chalk border border-carbon rounded">✏ Edit</button>
          <button onClick={onClose} className="px-2 py-1 text-footnote text-steel hover:text-chalk border border-carbon rounded">✕</button>
        </div>
      </div>
      <div className="space-y-1.5">
        <div className="flex gap-2">
          <input
            value={noteText}
            onChange={e => setNoteText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void handleLogNote() } }}
            placeholder="Quick note (Enter to save)…"
            className="flex-1 bg-void border border-carbon rounded px-3 py-1.5 text-footnote text-chalk placeholder:text-steel/50 focus:outline-none focus:border-signal"
          />
          <button onClick={handleLogNote} disabled={saving || !noteText.trim()}
            className="px-3 py-1.5 text-footnote bg-signal text-white rounded hover:bg-signal/90 disabled:opacity-40 shrink-0">
            {noteSaved ? '✓' : 'Save'}
          </button>
        </div>
        {savedNotes.length > 0 && (
          <div className="space-y-1 pl-1">
            {savedNotes.map((n, i) => (
              <div key={i} className="flex gap-2 text-footnote">
                <span className="text-steel shrink-0">{n.ts} 📝</span>
                <span className="text-chalk">{n.text}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function InvestorActions({ investor, onEdit, onBookingMsg, navigate }: {
  investor: Investor
  onEdit: (i: Investor) => void
  onBookingMsg: (msg: string) => void
  navigate: ReturnType<typeof useNavigate>
}) {
  const c = useThemeColors()
  const [calcomSending, setCalcomSending] = useState(false)

  async function handleBooking(e: React.MouseEvent) {
    e.stopPropagation()
    setCalcomSending(true)
    try {
      const result = await triggerCalcomInvestorFollowup(investor.id)
      onBookingMsg(`✓ Booking link sent to ${result.to}`)
    } catch (err) {
      const error = err as { status?: number; message?: string }
      onBookingMsg(error.status === 409 ? '⚠ Already booked' : `✗ Failed — ${error.message ?? 'error'}`)
    } finally { setCalcomSending(false) }
  }

  return (
    <>
      <button onClick={e => { e.stopPropagation(); onEdit(investor) }} title="Edit" style={{ padding: '2px 7px', fontSize: 11, color: c.textMuted, border: `1px solid ${c.border}`, borderRadius: 3, background: 'none', cursor: 'pointer' }}>✏</button>
      <button onClick={e => { e.stopPropagation(); navigate(`/dashboard/investors/${investor.id}`) }} title="Full detail" style={{ padding: '2px 7px', fontSize: 11, color: c.textMuted, border: `1px solid ${c.border}`, borderRadius: 3, background: 'none', cursor: 'pointer' }}>→</button>
      {investor.email && (
        <button onClick={e => void handleBooking(e)} disabled={calcomSending} title="Send booking link" style={{ padding: '2px 7px', fontSize: 11, color: c.textPrimary, border: `1px solid ${c.border}`, borderRadius: 3, background: c.carbon, cursor: 'pointer' }}>{calcomSending ? '…' : '📅'}</button>
      )}
    </>
  )
}

interface InvestorTableProps {
  investors: Investor[]
  onEdit: (investor: Investor) => void
  onBookingMsg: (msg: string) => void
  onToggleUpdates: (investor: Investor) => void
  onContactsChanged: (investorId: string, contacts: InvestorContact[]) => void
}

function InvestorTable({ investors, onEdit, onBookingMsg, onToggleUpdates, onContactsChanged }: InvestorTableProps) {
  const navigate = useNavigate()
  const c = useThemeColors()
  const [colWidths, setColWidths] = useState<number[]>(() => INV_COLS.map(col => col.defaultW))
  const [sortCol, setSortCol] = useState<string>('last_touch')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [hoveredId, setHoveredId] = useState<string | null>(null)
  const dragRef = useRef<{ colIdx: number; startX: number; startW: number } | null>(null)

  useEffect(() => {
    function onMouseMove(e: MouseEvent) {
      if (!dragRef.current) return
      const { colIdx, startX, startW } = dragRef.current
      const newW = Math.max(60, startW + (e.clientX - startX))
      setColWidths(prev => { const next = [...prev]; next[colIdx] = newW; return next })
    }
    function onMouseUp() { dragRef.current = null }
    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
    return () => { window.removeEventListener('mousemove', onMouseMove); window.removeEventListener('mouseup', onMouseUp) }
  }, [])

  function startResize(e: React.MouseEvent, colIdx: number) {
    e.preventDefault()
    dragRef.current = { colIdx, startX: e.clientX, startW: colWidths[colIdx] }
  }

  function handleSort(key: string) {
    if (sortCol === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortCol(key); setSortDir('desc') }
  }

  const sorted = [...investors].sort((a, b) => {
    const mul = sortDir === 'asc' ? 1 : -1
    switch (sortCol) {
      case 'firm': return mul * ((a.firm === 'Personal' ? a.name : a.firm).localeCompare(b.firm === 'Personal' ? b.name : b.firm))
      case 'stage': return mul * ((STAGE_ORDER[a.stage] ?? 0) - (STAGE_ORDER[b.stage] ?? 0))
      case 'fund_stage': return mul * ((a.fund_stage ?? '').localeCompare(b.fund_stage ?? ''))
      case 'check_size': return mul * ((a.check_size ?? '').localeCompare(b.check_size ?? ''))
      case 'invested': return mul * ((a.amount_invested ?? 0) - (b.amount_invested ?? 0))
      case 'last_touch': return mul * ((a.last_contacted ?? '').localeCompare(b.last_contacted ?? ''))
      case 'updates': return mul * (Number(a.receives_updates ?? false) - Number(b.receives_updates ?? false))
      default: return 0
    }
  })

  const gridTemplate = buildInvGridTemplate(colWidths)
  const cellStyle: React.CSSProperties = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', minWidth: 0 }

  return (
    <div style={{ overflowX: 'auto' }}>
      <div style={{ minWidth: colWidths.reduce((a, b) => a + b, 0) + 'px' }}>
        {/* Header */}
        <div style={{ display: 'grid', gridTemplateColumns: gridTemplate, background: c.bgPrimary, borderBottom: `1px solid ${c.carbon}`, position: 'sticky', top: 0, zIndex: 10 }}>
          {INV_COLS.map((col, i) => (
            <div key={col.key} style={{ position: 'relative', padding: '6px 8px', display: 'flex', alignItems: 'center', gap: 4 }}>
              {col.sortable ? (
                <button
                  onClick={() => handleSort(col.key)}
                  style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: sortCol === col.key ? c.textPrimary : c.textMuted, fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: 3, whiteSpace: 'nowrap' }}
                >
                  {col.label}
                  {sortCol === col.key && <span style={{ fontSize: 9 }}>{sortDir === 'asc' ? '▲' : '▼'}</span>}
                </button>
              ) : (
                <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: c.textMuted, fontWeight: 600 }}>{col.label}</span>
              )}
              {i < INV_COLS.length - 1 && (
                <div onMouseDown={e => startResize(e, i)}
                  style={{ position: 'absolute', right: 0, top: 0, width: 4, height: '100%', cursor: 'col-resize', zIndex: 1 }} />
              )}
            </div>
          ))}
        </div>

        {/* Rows */}
        {sorted.map(investor => {
          const isExpanded = expandedId === investor.id
          const color = stageColor(investor.stage)
          const cardTitle = investor.firm === 'Personal' ? investor.name : investor.firm
          const firstContact = investor.contacts?.[0]
          const cardSubtitle = firstContact?.name ?? (investor.firm !== 'Personal' && investor.name !== investor.firm ? investor.name : null)
          const extraContacts = (investor.contacts?.length ?? 0) > 1 ? investor.contacts.length - 1 : 0

          return (
            <div key={investor.id}>
              <div
                onClick={() => setExpandedId(isExpanded ? null : investor.id)}
                onDoubleClick={() => navigate(`/dashboard/investors/${investor.id}`)}
                onMouseEnter={() => setHoveredId(investor.id)}
                onMouseLeave={() => setHoveredId(null)}
                style={{ display: 'grid', gridTemplateColumns: gridTemplate, borderBottom: `1px solid ${c.carbon}`, cursor: 'pointer', background: isExpanded ? c.signalSubtle : hoveredId === investor.id ? 'rgba(255,255,255,0.03)' : undefined, transition: 'background 150ms' }}
              >
                {/* Firm / Contact */}
                <div style={{ ...cellStyle, padding: '7px 8px', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, width: '100%', overflow: 'hidden' }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: c.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cardTitle}</span>
                    {firstContact?.linkedin && (
                      <a href={firstContact.linkedin} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ fontSize: 10, color: c.textMuted, flexShrink: 0, textDecoration: 'none' }}>↗</a>
                    )}
                  </div>
                  {cardSubtitle && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, width: '100%', overflow: 'hidden' }}>
                      <span style={{ fontSize: 11, color: c.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cardSubtitle}</span>
                      {extraContacts > 0 && (
                        <span style={{ fontSize: 10, color: c.textMuted, opacity: 0.6, flexShrink: 0 }}>+{extraContacts} more</span>
                      )}
                    </div>
                  )}
                </div>

                {/* Stage */}
                <div style={{ ...cellStyle, padding: '7px 8px' }}>
                  <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 6px', borderRadius: 3, color, background: `${color}18`, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
                    {stageLabel(investor.stage)}
                  </span>
                </div>

                {/* Fund stage */}
                <div style={{ ...cellStyle, padding: '7px 8px' }}>
                  <span style={{ fontSize: 12, color: c.textMuted, textTransform: 'capitalize' }}>{investor.fund_stage ?? ''}</span>
                </div>

                {/* Check size */}
                <div style={{ ...cellStyle, padding: '7px 8px' }}>
                  <span style={{ fontSize: 12, color: c.textPrimary }}>{investor.check_size ?? ''}</span>
                </div>

                {/* Invested */}
                <div style={{ ...cellStyle, padding: '7px 8px', justifyContent: 'center' }}>
                  {investor.is_current_investor && investor.amount_invested && (
                    <span style={{ fontSize: 12, color: '#4ade80' }}>${(investor.amount_invested / 1000).toFixed(0)}K</span>
                  )}
                </div>

                {/* Last touch */}
                <div style={{ ...cellStyle, padding: '7px 8px', justifyContent: 'flex-end' }}>
                  <span style={{ fontSize: 11, color: c.textMuted }}>{timeAgo(investor.last_contacted)}</span>
                </div>

                {/* Updates */}
                <div style={{ ...cellStyle, padding: '7px 8px', justifyContent: 'center' }}>
                  <input
                    type="checkbox"
                    checked={investor.receives_updates ?? false}
                    onChange={e => { e.stopPropagation(); onToggleUpdates(investor) }}
                    onClick={e => e.stopPropagation()}
                    title={investor.receives_updates ? 'Receiving updates — click to remove' : 'Not receiving updates — click to add'}
                    style={{ width: 14, height: 14, cursor: 'pointer', accentColor: c.signal }}
                  />
                </div>

                {/* Next action */}
                <div style={{ ...cellStyle, padding: '7px 8px' }}>
                  {investor.next_action && (
                    <span style={{ fontSize: 11, color: c.signal, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>▶ {investor.next_action}</span>
                  )}
                </div>

                {/* Actions */}
                <div style={{ ...cellStyle, padding: '7px 8px', gap: 4, opacity: hoveredId === investor.id ? 1 : 0, transition: 'opacity 120ms' }}>
                  <InvestorActions investor={investor} onEdit={onEdit} onBookingMsg={onBookingMsg} navigate={navigate} />
                </div>
              </div>

              {isExpanded && (
                <InvestorExpandPanel
                  investor={investor}
                  onEdit={onEdit}
                  onClose={() => setExpandedId(null)}
                  onContactsChanged={onContactsChanged}
                />
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default function AdminInvestorCRM() {
  const { user, isLoaded } = useUser()
  const [investors, setInvestors] = useState<Investor[]>([])
  const [loading, setLoading] = useState(true)
  const [stageFilter, setStageFilter] = useState<string>('all')
  const [search, setSearch] = useState('')
  const [showAdd, setShowAdd] = useState(false)
  const [editInvestor, setEditInvestor] = useState<Investor | null>(null)
  const [toast, setToast] = useState<string | null>(null)

  const email = user?.primaryEmailAddress?.emailAddress ?? user?.emailAddresses?.[0]?.emailAddress ?? ''
  const isAdmin = isLoaded && !!user && ADMIN_EMAILS.includes(email)

  const loadInvestors = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getInvestors(stageFilter === 'all' ? undefined : stageFilter)
      setInvestors(data)
    } catch {
      // silent
    } finally {
      setLoading(false)
    }
  }, [stageFilter])

  useEffect(() => {
    if (isAdmin) void loadInvestors()
  }, [isAdmin, loadInvestors])

  const filteredInvestors = search.trim()
    ? investors.filter(i =>
        i.firm?.toLowerCase().includes(search.toLowerCase()) ||
        i.name?.toLowerCase().includes(search.toLowerCase()),
      )
    : investors

  if (!isLoaded) return <div className="min-h-screen bg-void" />

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center">
        <p className="text-body text-steel">Access denied</p>
      </div>
    )
  }

  function handleInvestorCreated(investor: Investor) {
    setShowAdd(false)
    setInvestors(prev => [investor, ...prev])
    showToast('Investor added')
  }

  function handleInvestorSaved(updated: Investor) {
    setInvestors(prev => prev.map(i => i.id === updated.id ? { ...i, ...updated } : i))
    setEditInvestor(null)
    showToast('Saved')
  }

  function handleContactsChanged(investorId: string, contacts: InvestorContact[]) {
    setInvestors(prev => prev.map(i => i.id === investorId ? { ...i, contacts } : i))
    setEditInvestor(prev => prev && prev.id === investorId ? { ...prev, contacts } : prev)
  }

  async function handleToggleUpdates(investor: Investor) {
    try {
      const updated = await updateInvestor(investor.id, { receives_updates: !investor.receives_updates })
      setInvestors(prev => prev.map(i => i.id === investor.id ? { ...i, ...updated } : i))
    } catch {
      showToast('Failed to update')
    }
  }

  function showToast(msg: string) {
    setToast(msg)
    setTimeout(() => setToast(null), 4000)
  }

  const ALL_STAGES = [{ key: 'all', label: 'All' }, ...STAGES.map(s => ({ key: s.key, label: s.label }))]

  return (
    <>
      {showAdd && (
        <AddInvestorModal
          onClose={() => setShowAdd(false)}
          onSuccess={handleInvestorCreated}
        />
      )}

      {editInvestor && (
        <EditInvestorPanel
          investor={editInvestor}
          onClose={() => setEditInvestor(null)}
          onSaved={handleInvestorSaved}
          onContactsChanged={handleContactsChanged}
        />
      )}

      {toast && (
        <div className="fixed bottom-6 right-6 z-50 bg-signal text-white text-body-sm px-4 py-3 rounded shadow-lg">
          {toast}
        </div>
      )}

      <div className="flex-1 p-6 text-chalk">
        <div className="max-w-6xl space-y-4">

          {/* Header */}
          <div className="flex items-center gap-3">
            <h1 className="text-h1 text-chalk">Investor CRM</h1>
            {!loading && (
              <span className="px-2 py-0.5 rounded text-footnote bg-carbon text-steel font-medium">
                {filteredInvestors.length}
              </span>
            )}
            <button onClick={loadInvestors} className="ml-auto text-body-sm text-signal hover:underline">
              Refresh
            </button>
            <button
              onClick={() => setShowAdd(true)}
              className="px-3 py-1.5 text-body-sm bg-signal text-white rounded hover:bg-signal/90 transition-colors"
            >
              + Add Investor
            </button>
          </div>

          {/* Filter bar */}
          <div className="space-y-2">
            {/* Stage filter tabs */}
            <div className="flex flex-wrap gap-1.5">
              {ALL_STAGES.map(s => (
                <button key={s.key} onClick={() => setStageFilter(s.key)}
                  className={`text-footnote px-2.5 py-1 rounded transition-colors ${
                    stageFilter === s.key ? 'bg-signal text-white' : 'text-steel hover:text-chalk'
                  }`}>
                  {s.label}
                </button>
              ))}
            </div>
            {/* Search */}
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search firm or name…"
              className="w-full max-w-sm bg-obsidian border border-carbon rounded px-3 py-1.5 text-body-sm text-chalk placeholder:text-steel focus:outline-none focus:border-signal" />
          </div>

          {/* Investor table */}
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-signal" />
            </div>
          ) : filteredInvestors.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-steel text-body">No investors found.</p>
            </div>
          ) : (
            <div className="border border-carbon rounded overflow-hidden">
              <InvestorTable
                investors={filteredInvestors}
                onEdit={setEditInvestor}
                onBookingMsg={showToast}
                onToggleUpdates={handleToggleUpdates}
                onContactsChanged={handleContactsChanged}
              />
            </div>
          )}
        </div>
      </div>
    </>
  )
}
